import { apiClient } from '../index';

// API 응답 타입
interface ApiResponse<T> {
  code: number;
  status: string;
  message: string;
  data: T;
}

// 강의 질문 답변 목록 파라미터
export interface LectureQAManagementListParams {
  page: number;
  size: number;
  lectureCategoryId?: number;
  lectureId?: number;
  from?: string;
  to?: string;
  status?: boolean;
  query?: string;
}

// 강의 질문 답변 아이템
export interface LectureQAManagementItem {
  id: number;
  title: string;
  answered: boolean;
  writer_name: string;
  created_at: string;
}

// 강의 질문 상세 정보
export interface LectureQADetail {
  post_id: number;
  title: string;
  content: string;
  post_images: string[];
  writer_name: string;
  writer_profile_image_url: string | null;
  created_at: string;
  answered: boolean;
  answer_id: number | null;
  answer_content: string | null;
  answer_images: string[];
  answer_writer_name: string | null;
  answer_writer_profile_image_url: string | null;
  answer_created_at: string | null;
  answer_updated_at: string | null;
}

// 첨부파일
export interface Attachment {
  id: number;
  filename: string;
  url: string;
  size: number;
  type: string;
}

// 강연자 정보
export interface Mentor {
  id: number;
  name: string;
  email: string;
  department: string;
  is_temp: boolean;
  created_at: string;
}

// 페이지네이션 정보
export interface PageInfo {
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
}

// 목록 응답
export interface LectureQAManagementListResponse {
  content: LectureQAManagementItem[];
  pageable: PageInfo;
  totalElements: number;
  totalPages: number;
  last: boolean;
  size: number;
  number: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  numberOfElements: number;
  first: boolean;
  empty: boolean;
}

// 강의 질문 답변 관리 API
export const lectureQAManagementApi = {
  // 강의 질문 답변 목록 조회
  getLectureQAList: async (params: LectureQAManagementListParams): Promise<LectureQAManagementListResponse> => {
    try {
      const isSearch = !!(params.query && params.query.trim());
      const queryParams = new URLSearchParams();
      queryParams.append('page', params.page.toString());
      queryParams.append('size', params.size.toString());

      if (isSearch) {
        queryParams.append('search', params.query!.trim());
      }
      if (params.lectureCategoryId) {
        queryParams.append('lectureCategoryId', params.lectureCategoryId.toString());
      }
      if (!isSearch && params.lectureId) {
        queryParams.append('lectureId', params.lectureId.toString());
      }
      if (params.from) {
        queryParams.append('from', params.from);
      }
      if (params.to) {
        queryParams.append('to', params.to);
      }
      if (params.status !== undefined) {
        queryParams.append('status', params.status.toString());
      }

      // 검색 여부와 무관하게 일반 list 엔드포인트 사용 (search 파라미터로 검색)
      const endpoint = '/api/admin/posts/lectures/list';
      const { data: result } = await apiClient.get<ApiResponse<LectureQAManagementListResponse>>(
        `${endpoint}?${queryParams.toString()}`,
        { withCredentials: true }
      );

      return result.data;
    } catch (error: any) {
      /// console.error('Error fetching lecture QA list:', error);
      throw new Error(error.response?.data?.message || '강의 질문 답변 목록을 불러오는데 실패했습니다.');
    }
  },

  // 강의 질문 답변 삭제
  deleteLectureQA: async (id: number): Promise<void> => {
    try {
      await apiClient.delete(`/api/admin/posts/lectures/${id}`, {
        withCredentials: true
      });
    } catch (error: any) {
      /// console.error('Error deleting lecture QA:', error);
      throw new Error(error.response?.data?.message || '강의 질문 답변 삭제에 실패했습니다.');
    }
  },

  // 강의 질문 답변 상태 변경
  toggleLectureQAStatus: async (id: number, status: string): Promise<void> => {
    try {
      await apiClient.patch(`/api/admin/posts/lectures/${id}/status`, 
        { status },
        { withCredentials: true }
      );
    } catch (error: any) {
      /// console.error('Error toggling lecture QA status:', error);
      throw new Error(error.response?.data?.message || '강의 질문 답변 상태 변경에 실패했습니다.');
    }
  },

  // 강의 질문 답변 등록
  submitLectureQAAnswer: async (id: number, content: string, writerId: number): Promise<void> => {
    try {
      await apiClient.post(`/api/admin/posts/lectures/${id}/answer`, 
        { content, writerId },
        { withCredentials: true }
      );
    } catch (error: any) {
      /// console.error('Error submitting lecture QA answer:', error);
      throw new Error(error.response?.data?.message || '강의 질문 답변 등록에 실패했습니다.');
    }
  },

  // 강의 질문 상세 조회
  getLectureQADetail: async (id: number): Promise<LectureQADetail> => {
    try {
      const { data: result } = await apiClient.get<ApiResponse<LectureQADetail>>(
        `/api/admin/posts/lectures/${id}`,
        { withCredentials: true }
      );
      return result.data;
    } catch (error: any) {
      /// console.error('Error fetching lecture QA detail:', error);
      throw new Error(error.response?.data?.message || '강의 질문 상세 정보를 불러오는데 실패했습니다.');
    }
  },

  // 강의 질문 답변 수정
  updateLectureQAAnswer: async (postId: number, replyId: number, content: string, writerId: number): Promise<void> => {
    try {
      await apiClient.put(`/api/admin/posts/lectures/${postId}/answer/${replyId}`, 
        { content, writerId },
        { withCredentials: true }
      );
    } catch (error: any) {
      /// console.error('Error updating lecture QA answer:', error);
      throw new Error(error.response?.data?.message || '강의 질문 답변 수정에 실패했습니다.');
    }
  },

  // 강의 질문 답변 삭제
  deleteLectureQAAnswer: async (postId: number, replyId: number): Promise<void> => {
    try {
      await apiClient.delete(`/api/admin/posts/lectures/${postId}/answer/${replyId}`, 
        { withCredentials: true }
      );
    } catch (error: any) {
      /// console.error('Error deleting lecture QA answer:', error);
      throw new Error(error.response?.data?.message || '강의 질문 답변 삭제에 실패했습니다.');
    }
  }
};

// 강연자 목록 API
export const mentorApi = {
  // 강연자 목록 조회
  getMentors: async (): Promise<Mentor[]> => {
    try {
      const { data: result } = await apiClient.get<ApiResponse<any[]>>(
        '/api/persons/list/lecturers',
        { withCredentials: true }
      );
      /// console.log('강연자 목록 API raw response (persons/list/lecturers):', result);
      
      // 새 응답: data가 배열 형태
      const list = Array.isArray(result?.data) ? result.data : [];
      return list.map((item: any): Mentor => ({
        id: item.id,
        name: item.name,
        email: item.email ?? '',
        department: item.department ?? '',
        is_temp: false,
        created_at: item.created_at ?? ''
      }));
    } catch (error: any) {
      /// console.error('Error fetching mentors:', error);
      throw new Error(error.response?.data?.message || '강연자 목록을 불러오는데 실패했습니다.');
    }
  }
};

// 댓글 첨부파일 API
export const replyAttachmentApi = {
  // 첨부파일 등록
  uploadAttachment: async (replyId: number, file: File): Promise<Attachment> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const { data: result } = await apiClient.post<ApiResponse<Attachment>>(
        `/api/reply/attachments`,
        formData,
        { 
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return result.data;
    } catch (error: any) {
      /// console.error('Error uploading attachment:', error);
      throw new Error(error.response?.data?.message || '첨부파일 업로드에 실패했습니다.');
    }
  },

  // 첨부파일 삭제
  deleteAttachment: async (replyId: number, imageUrls: string[]): Promise<void> => {
    try {
      const formData = new FormData();
      imageUrls.forEach(url => formData.append('imageUrls', url));
      await apiClient.delete(`/api/reply/${replyId}/attachments`,
        { data: formData, withCredentials: true }
      );
    } catch (error: any) {
      /// console.error('Error deleting attachment:', error);
      throw new Error(error.response?.data?.message || '첨부파일 삭제에 실패했습니다.');
    }
  }
};
